/**
 * Walk 4 (#2268) — the staff actions on one onboarding file: the buttons beside each open item,
 * their dialogs, and the result toast. Card 2404 adds an action on every item: the decisions on a
 * person, each a dialog in the approved ResolveDialog pattern.
 *
 * <h3>Who may act is the ENGINE's answer, never this bundle's</h3>
 * <p>Every action reference on an open item carries `allowed`, computed on the server for this
 * viewer from the tenant's role grants (the role tokens are declared in the workflow, and the
 * route checks them again on the POST). A button is drawn ONLY when `allowed` is true; a viewer
 * with no role grant sees every item and no buttons. Nothing here names a role.</p>
 *
 * <h3>What a button says and how it asks is the ENGINE's answer too (card 3357)</h3>
 * <p>This screen never switches on an action id. Each reference carries its `label` (the button),
 * its `dialog` kind and its `confirm` sentence — the workflow's declaration, else the platform's
 * own words for the verb — and the page draws by the KIND:</p>
 * <ul>
 *   <li><b>none</b> — runs on the click (a deep link asks first: nobody has clicked).</li>
 *   <li><b>confirm</b> — the sentence, verbatim, and one button.</li>
 *   <li><b>reason</b> — the sentence and a REQUIRED reason; the button stays off until there is
 *       one, and the reason is posted as `reason`.</li>
 *   <li><b>form-ref</b> — a link to the reference's `url` (a staff form); nothing is posted.</li>
 *   <li>The onboarding family's own dialogs, which only the platform sets: <b>look_through</b>
 *       (who owns a company), <b>email</b> (one checked address, which sends their form),
 *       <b>pick_party</b> (choose one of an item's people), <b>removal</b> (the people the client
 *       removed, accepted at once) and <b>result</b> (a person's check results; nothing is
 *       posted).</li>
 * </ul>
 * <p>The copy rule, one for every kind: `label` is the button AND the dialog's primary button;
 * `confirm` is the dialog's body, verbatim; the heading is the label. Where the platform's own
 * words for a verb say more (a heading that names the person, a reason prompt, the line said once
 * it is done), the engine sends them as `wording`, and they stand in for those three. A page served
 * by an older engine with no `label` draws the id in words with a plain confirm.</p>
 *
 * <p>Every post goes to the reference's own route (`postStaffAction`), with the reference's own
 * `target` as the body plus what the dialog collected. Each result is a toast, read by the answer's
 * shape (a reminder says who was reached; a look-through who was sent a check). A 403 says who CAN
 * ("Only a@x or b@y can do this") — inside the dialog when one is open, so the names stay on
 * screen; a 409 says "Already done"; and the file is refetched after every answer so the page shows
 * what is true. The panel's actions (the engine's `panel_actions`: "Send reminders" to everyone
 * owed, once two or more items can be chased) are drawn the same way.</p>
 *
 * <h3>Deep links</h3>
 * <p>`/onboarding/:jobId?action=<action>&party=<key>` (the Google Chat alerts link here) opens that
 * action's dialog once the file has loaded — only when this viewer may take it. An action that runs
 * on the click, or a form link, opened this way asks first, because nobody has clicked anything
 * yet. A link whose item is not on the file says it is looking — the item may not be open yet, or
 * may already have been dealt with — and re-reads the file every 15 seconds for up to two
 * minutes.</p>
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Icon, Input, Modal, Select, Textarea, Toast } from "@ardept/design-system";
import { describeActionError, postStaffAction, useJobAction } from "../api/portalQueries.js";
import type {
  JobStatusActionDialog,
  JobStatusActionRef,
  JobStatusActionWording,
  JobStatusDetailResponse,
  JobStatusOpenItem,
  JobStatusOpenItemPerson,
  JobStatusPerson,
  LookThroughBand,
  LookThroughConfirmedVia,
  RecordLookThroughRequest,
  RecordLookThroughResponse,
  SendReminderResponse,
} from "../api/types.js";
import { PersonStatus, hitLine } from "./personStatus.js";

// ── Words ────────────────────────────────────────────────────────────────────────────────

/** The bands, as the design's select says them, in the contract's values. */
export const BAND_OPTIONS: readonly { value: LookThroughBand; label: string }[] = [
  { value: "25_50", label: "25–50%" },
  { value: "50_75", label: "50–75%" },
  { value: "75_plus", label: "75% or more" },
  { value: "other_control", label: "Other control" },
];

/** How it was confirmed, as the design's select says it, in the contract's values. */
export const CONFIRMED_VIA_OPTIONS: readonly { value: LookThroughConfirmedVia; label: string }[] = [
  { value: "companies_house", label: "The company's Companies House register" },
  { value: "client_documents", label: "The client told us, with documents" },
  { value: "group_structure_chart", label: "Group structure chart from the client" },
];

/** An unresolved deep link: what the page says while it waits, how often it re-reads, how long. */
export const DEEP_LINK_PENDING =
  "Looking for this item on the file — it may already have been dealt with. This page re-checks for a couple of minutes.";
export const DEEP_LINK_GAVE_UP =
  "That item has not appeared on this file. It may already have been dealt with — refresh to check.";
export const DEEP_LINK_POLL_MS = 15_000;
// Two minutes: the engine opens the item on the same pass that decides it is owed, so a link that
// is still unresolved after that is almost always an item someone already dealt with.
export const DEEP_LINK_WAIT_MS = 2 * 60_000;

/** How long an error toast stays up: long enough to read who CAN do something. */
const ERROR_TOAST_MS = 12_000;

/**
 * Card 3357 — the page's own words for a dialog the engine sent no sentence for: a confirm, a form
 * link opened from a deep link, a reason dialog, and the line said once an action is done. The
 * platform's words for its own verbs come from the engine; these are only what is left when an
 * engine (or a declaration) says nothing.
 */
export const GENERIC_CONFIRM_BODY =
  "This happens straight away, and it is recorded in the history against your name.";
export const GENERIC_FORM_LINK_BODY = "This opens the staff form in a new tab.";
export const GENERIC_REASON_BODY = "Say why: your reason is kept in the file's history.";
export const GENERIC_REASON_PROMPT = "Your reason";

/** The line said once an action with no `wording.done` is done. */
export function doneLine(label: string): string {
  return `Done. "${label}" is recorded in the history.`;
}

/**
 * `clear_party` → "Clear party": an action id said in words, for a reference an older engine sent
 * no label for. Never the raw id (staff forms speak English, form rule 7).
 */
export function humanise(action: string): string {
  const words = action.replace(/[_:-]+/g, " ").trim();
  return words === "" ? "Act on this" : words.charAt(0).toUpperCase() + words.slice(1);
}

/** How one reference is drawn: the engine's words and dialog kind, or the page's fallback. */
export interface ActionRendering {
  label: string;
  dialog: JobStatusActionDialog;
  confirm: string | undefined;
  wording: JobStatusActionWording;
}

/**
 * The reference's rendering, as the engine sent it. An older engine that sends no `label` reads as
 * the id in words, and no `dialog` as a plain confirm.
 */
export function renderingOf(ref: JobStatusActionRef): ActionRendering {
  const label = ref.label !== undefined && ref.label.trim() !== "" ? ref.label : humanise(ref.action);
  return { label, dialog: ref.dialog ?? "confirm", confirm: ref.confirm, wording: ref.wording ?? {} };
}

/** A dialog's heading: the engine's own, else the label. */
function headingOf(r: ActionRendering): string {
  return r.wording.title ?? r.label;
}

/** A dialog's primary button: the engine's own, else the label. */
function submitOf(r: ActionRendering): string {
  return r.wording.submit ?? r.label;
}

/** The dialog kinds that open in place from another dialog (View result's "next" buttons). */
const IN_PLACE_KINDS: ReadonlySet<JobStatusActionDialog> = new Set(["none", "confirm", "reason"]);

/** The dialog kinds that are about ONE person, looked up by their party key. */
const PERSON_KINDS: ReadonlySet<JobStatusActionDialog> = new Set(["result", "email"]);

/** A usable email address: something@something.something, no spaces. The engine checks it again. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when `value` looks like an email address a form could be sent to. */
export function looksLikeEmail(value: string): boolean {
  return EMAIL_SHAPE.test(value.trim());
}

/** The person an action is about: the reference's own party, else the item's owner. */
function partyOf(item: JobStatusOpenItem, ref: JobStatusActionRef): string | null {
  return ref.target.party_key ?? item.owed_by.party_key ?? null;
}

/**
 * A person's full name, from the richest source the file holds: the people table, the party
 * ledger, then the grouped item's own list. Never a party key.
 */
export function personName(
  detail: JobStatusDetailResponse | undefined,
  partyKey: string | null,
  item?: JobStatusOpenItem,
): string {
  if (partyKey === null) return "this person";
  const name =
    detail?.people?.find((person) => person.party_key === partyKey)?.name ??
    detail?.parties.find((party) => party.iteration_key === partyKey)?.display_name ??
    item?.people?.find((person) => person.party_key === partyKey)?.name;
  return typeof name === "string" && name.trim() !== "" ? name : "this person";
}

/** "a", "a and b", "a, b and c". */
function joinAnd(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The exact body for an action: the reference's own `target`, never re-derived here, plus what its
 * dialog collected (a reason). The engine resolves `party_key` / `item_key` against the SAME plan
 * it offered the button from — a reminder to THAT person, `{ all: true }` only on the panel's
 * "everyone" reference — so posting anything else (a guessed party) would act on people the
 * staffer did not click on.
 */
export function actionBody(ref: JobStatusActionRef, extra: Record<string, unknown> = {}): Record<string, unknown> {
  return { ...ref.target, ...extra };
}

/** True when the reference names someone or something to act on. */
function namesATarget(ref: JobStatusActionRef): boolean {
  return ref.target.party_key !== undefined || ref.target.item_key !== undefined || ref.target.all === true;
}

/** What a reminder result says. */
export function reminderResult(result: SendReminderResponse): { message: string; ok: boolean } {
  const names = result.sent.map((entry) => entry.name);
  const missed = result.skipped
    .map((entry) => entry.name)
    .filter((name) => typeof name === "string" && name.trim() !== "");
  if (names.length === 0) {
    if (result.skipped.length === 0) {
      return { message: "No reminder was sent: nobody is waiting to be reminded.", ok: false };
    }
    // One skip carries the engine's own reason ("They don't owe anything.", "No email address…").
    const only = result.skipped.length === 1 ? result.skipped[0] : undefined;
    return {
      message:
        only !== undefined && missed.length === 1
          ? `No reminder was sent to ${missed[0]}. ${only.reason}`
          : missed.length > 0
            ? `No reminder was sent: ${joinAnd(missed)} could not be reached.`
            : "No reminder was sent: nobody on this item can be reached yet.",
      ok: false,
    };
  }
  const head = names.length === 1 ? `Reminder sent to ${names[0]}` : `Reminders sent to ${joinAnd(names)}`;
  const skippedCount = result.skipped.length;
  const tail =
    skippedCount === 0
      ? "."
      : missed.length === skippedCount
        ? `. Could not reach ${joinAnd(missed)}.`
        : `. ${skippedCount} ${skippedCount === 1 ? "person" : "people"} could not be reached.`;
  return { message: `${head}${tail}`, ok: true };
}

/**
 * What a recorded look-through says. The added people whose identity-form email failed
 * (`link_failed`, by party key) are named, not counted as sent: their forms are still owed and a
 * staffer has to reach them another way.
 */
export function lookThroughResult(result: RecordLookThroughResponse): string {
  const failedKeys = new Set(result.link_failed ?? []);
  const sent = result.added_parties.filter((party) => !failedKeys.has(party.party_key));
  const failed = result.added_parties.filter((party) => failedKeys.has(party.party_key));
  // A failed key the engine did not also list as added still counts, by number.
  const unnamed = failedKeys.size - failed.length;
  let message = "Look-through recorded. The approval hold is cleared.";
  if (sent.length > 0) message += ` Identity checks sent to ${joinAnd(sent.map((p) => p.name))}.`;
  if (failed.length > 0 || unnamed > 0) {
    const who =
      unnamed === 0
        ? joinAnd(failed.map((p) => p.name))
        : `${failedKeys.size} ${failedKeys.size === 1 ? "person" : "people"}`;
    message += ` The identity form could not be emailed to ${who}. Check the email address and send a reminder.`;
  }
  return message;
}

/** A reminder's answer: who was reached, and who was not. */
function isReminderResult(value: unknown): value is SendReminderResponse {
  if (typeof value !== "object" || value === null) return false;
  const v = value as { sent?: unknown; skipped?: unknown };
  return Array.isArray(v.sent) && Array.isArray(v.skipped);
}

/** A look-through's answer: who was added, and whose form could not be emailed. */
function isLookThroughResult(value: unknown): value is RecordLookThroughResponse {
  if (typeof value !== "object" || value === null) return false;
  return Array.isArray((value as { added_parties?: unknown }).added_parties);
}

/**
 * Card 3357 — what the page says once an action is done, read by the ANSWER's shape: a reminder
 * says who was reached, a look-through who was sent a check; anything else says the engine's done
 * line for the action (`wording.done`), else the page's generic one.
 */
export function resultWords(ref: JobStatusActionRef, result: unknown): { message: string; ok: boolean } {
  if (isReminderResult(result)) return reminderResult(result);
  if (isLookThroughResult(result)) return { message: lookThroughResult(result), ok: true };
  const r = renderingOf(ref);
  return { message: r.wording.done ?? doneLine(r.label), ok: true };
}

/** The allowed references on an item, in the order the engine sent them. */
function allowedRefs(item: JobStatusOpenItem): JobStatusActionRef[] {
  return item.actions.filter((ref) => ref.allowed);
}

/**
 * The allowed references that get a button, in the engine's order, judged by their dialog KIND.
 * Card 2404: a decision about one person with nobody to act on is dropped rather than drawn as a
 * button that cannot post (and so is any reference that names no one and nothing); a grouped
 * item's pick-a-person action is ONE button (the dialog picks the person), however many references
 * the engine sent for it, and needs someone to pick; a form link needs its link.
 */
export function drawableRefs(item: JobStatusOpenItem): JobStatusActionRef[] {
  const picked = new Set<string>();
  return allowedRefs(item).filter((ref) => {
    const { dialog } = renderingOf(ref);
    if (PERSON_KINDS.has(dialog)) return partyOf(item, ref) !== null;
    if (dialog === "form-ref") return ref.url !== undefined;
    if (dialog === "pick_party") {
      if (picked.has(ref.action) || (item.people ?? []).length === 0) return false;
      picked.add(ref.action);
      return true;
    }
    return namesATarget(ref);
  });
}

// ── The dialogs ──────────────────────────────────────────────────────────────────────────

/** One editable row of the look-through form. `id` is React's key, never sent. */
interface ControllerDraft {
  id: number;
  name: string;
  band: LookThroughBand;
  email: string;
}

/**
 * "Record the look-through" — the approved ResolveDialog screen. Builds EXACTLY the contract's
 * body; the page posts it.
 */
export function LookThroughDialog({
  ownerName,
  clientName,
  partyKey,
  viewer,
  pending,
  error,
  onCancel,
  onSave,
}: {
  ownerName: string;
  clientName: string;
  partyKey: string;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (body: RecordLookThroughRequest) => void;
}): JSX.Element {
  const nextId = useRef(1);
  const [rows, setRows] = useState<ControllerDraft[]>([
    { id: 0, name: "", band: "25_50", email: "" },
  ]);
  const [confirmedVia, setConfirmedVia] = useState<LookThroughConfirmedVia>("companies_house");
  const [notes, setNotes] = useState("");
  const [sendChecks, setSendChecks] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);

  const update = (id: number, patch: Partial<ControllerDraft>): void =>
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const save = (): void => {
    const named = rows.map((row) => ({ ...row, name: row.name.trim(), email: row.email.trim() }));
    if (named.length === 0 || named.some((row) => row.name === "")) {
      setProblem("Add the full name of each person who owns or controls it.");
      return;
    }
    const badEmail = named.find((row) => sendChecks && row.email !== "" && !row.email.includes("@"));
    if (badEmail !== undefined) {
      setProblem(`Check the email address for ${badEmail.name}.`);
      return;
    }
    setProblem(null);
    onSave({
      party_key: partyKey,
      controllers: named.map((row) => ({
        name: row.name,
        band: row.band,
        email: sendChecks && row.email !== "" ? row.email : null,
      })),
      confirmed_via: confirmedVia,
      notes: notes.trim() === "" ? null : notes.trim(),
      send_identity_checks: sendChecks,
    });
  };

  const shown = problem ?? error;

  return (
    <Modal
      open
      onClose={onCancel}
      dismissible={!pending}
      title="Record the look-through"
      description={`${ownerName} owns 25% or more of ${clientName}. Record the people who own or control it.`}
      closeLabel="Close"
      className="modal--wide"
      footer={
        <div className="lt-foot">
          <span className="lt-hint">
            Recorded as {viewer ?? "you"}, today. This clears the approval hold.
          </span>
          <div className="lt-foot__buttons">
            <Button variant="ghost" onClick={onCancel} disabled={pending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={pending}>
              {pending ? "Saving…" : "Save look-through"}
            </Button>
          </div>
        </div>
      }
    >
      <form
        className="lt-form"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <fieldset className="lt-people">
          <legend className="lt-label">Who owns or controls it?</legend>
          {rows.map((row, index) => (
            <div key={row.id} className={`lt-person${sendChecks ? " lt-person--email" : ""}`}>
              <Input
                variant="text"
                type="text"
                aria-label={`Full name, person ${index + 1}`}
                placeholder="Full name"
                value={row.name}
                autoFocus={index === 0}
                onChange={(event) => update(row.id, { name: event.target.value })}
              />
              {sendChecks && (
                <Input
                  variant="text"
                  type="email"
                  aria-label={`Email, person ${index + 1}`}
                  placeholder="Email (optional)"
                  value={row.email}
                  onChange={(event) => update(row.id, { email: event.target.value })}
                />
              )}
              <Select
                aria-label={`Their control, person ${index + 1}`}
                options={BAND_OPTIONS}
                value={row.band}
                onValueChange={(band) => update(row.id, { band })}
              />
              <button
                type="button"
                className="lt-remove"
                aria-label={`Remove person ${index + 1}`}
                disabled={rows.length === 1}
                onClick={() => setRows((current) => current.filter((r) => r.id !== row.id))}
              >
                <Icon name="x" size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            className="lt-add"
            onClick={() =>
              setRows((current) => [
                ...current,
                { id: nextId.current++, name: "", band: "25_50", email: "" },
              ])
            }
          >
            <Icon name="plus" size={14} aria-hidden="true" />
            Add a person
          </Button>
        </fieldset>

        <label className="lt-field">
          <span className="lt-label">How did you confirm this?</span>
          <Select
            options={CONFIRMED_VIA_OPTIONS}
            value={confirmedVia}
            onValueChange={setConfirmedVia}
          />
        </label>

        <label className="lt-field">
          <span className="lt-label">
            Notes <span className="lt-optional">(optional)</span>
          </span>
          <Textarea
            rows={3}
            placeholder="What you checked and where"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>

        <label className="lt-check">
          <input
            type="checkbox"
            checked={sendChecks}
            onChange={(event) => setSendChecks(event.target.checked)}
          />
          <span>
            <span className="lt-check__title">Send identity checks to these people</span>
            <span className="lt-hint">
              Anyone who indirectly owns 25% or more gets their own identity form and SmartSearch
              check, like a director.
            </span>
          </span>
        </label>

        {shown !== null && (
          <p className="lt-error" role="alert">
            {shown}
          </p>
        )}
      </form>
    </Modal>
  );
}

/** A small confirm: a sentence, and one button that does it (or a link that goes there). */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  href,
  pending,
  error = null,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  href?: string;
  pending: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <Modal
      open
      onClose={onCancel}
      dismissible={!pending}
      title={title}
      closeLabel="Close"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          {href !== undefined ? (
            <a
              className="btn btn--primary"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCancel}
            >
              {confirmLabel}
            </a>
          ) : (
            <Button variant="primary" onClick={onConfirm} disabled={pending} autoFocus>
              {pending ? "Working…" : confirmLabel}
            </Button>
          )}
        </>
      }
    >
      <p className="lt-confirm">{body}</p>
      {error !== null && (
        <p className="lt-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

// ── Card 2404: the decisions on a person ────────────────────────────────────────────────────
//
// Every dialog below is the approved ResolveDialog screen's pattern (the walk-4 design,
// https://claude.ai/artifact/D5umzTyYDMcE3xeEqwE8vB): a title and a line under it, the body, and a
// footer that says who it is recorded as, with Cancel and the one primary button. Built from the
// design system's Modal, Input, Textarea, Select and Button, in the look-through dialog's layout
// classes, so the six read as one family.

/** The ResolveDialog shell: head, body, and a "Recorded as <you>, today." footer. */
export function ResolveDialog({
  title,
  description,
  viewer,
  note,
  primaryLabel,
  pendingLabel = "Saving…",
  pending,
  canSubmit = true,
  error,
  onCancel,
  onSubmit,
  children,
}: {
  title: string;
  /** The line under the title; none when omitted. */
  description?: ReactNode;
  viewer: string | null;
  /** Said after "Recorded as <you>, today." — what the act does beyond being recorded. */
  note?: string;
  primaryLabel: string;
  pendingLabel?: string;
  pending: boolean;
  canSubmit?: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: () => void;
  children: ReactNode;
}): JSX.Element {
  const submit = (): void => {
    if (canSubmit && !pending) onSubmit();
  };
  return (
    <Modal
      open
      onClose={onCancel}
      dismissible={!pending}
      title={title}
      description={description}
      closeLabel="Close"
      className="modal--wide"
      footer={
        <div className="lt-foot">
          <span className="lt-hint">
            Recorded as {viewer ?? "you"}, today.{note !== undefined ? ` ${note}` : ""}
          </span>
          <div className="lt-foot__buttons">
            <Button variant="ghost" onClick={onCancel} disabled={pending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} disabled={pending || !canSubmit}>
              {pending ? pendingLabel : primaryLabel}
            </Button>
          </div>
        </div>
      }
    >
      <form
        className="lt-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {children}
        {error !== null && (
          <p className="lt-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}

/**
 * View result — what the identity check and the screening found for one person, from the file as
 * last read (`people[]`): nothing is posted. Card 2404: the failed check's reason in the engine's
 * plain words, and each watchlist match. From here the staffer can go straight on to the item's
 * other allowed decisions (Clear, Ask them to re-run).
 */
export function ViewResultDialog({
  person,
  fallbackName,
  next,
  onClose,
}: {
  person: JobStatusPerson | undefined;
  fallbackName: string;
  /** The item's other allowed decisions, as buttons that switch to that dialog. */
  next: readonly { label: string; open: () => void }[];
  onClose: () => void;
}): JSX.Element {
  const hits = person?.screening.hits ?? [];
  return (
    <Modal
      open
      onClose={onClose}
      title="What the checks found"
      description={
        person === undefined
          ? fallbackName
          : person.role_label.trim() === ""
            ? person.name
            : `${person.name} · ${person.role_label}`
      }
      closeLabel="Close"
      className="modal--wide"
      footer={
        <div className="lt-foot">
          <span className="lt-hint">Viewing a result records nothing.</span>
          <div className="lt-foot__buttons">
            {next.map((action) => (
              <Button key={action.label} variant="ghost" onClick={action.open}>
                {action.label}
              </Button>
            ))}
            <Button variant="primary" onClick={onClose} autoFocus>
              Close
            </Button>
          </div>
        </div>
      }
    >
      {person === undefined ? (
        <p className="lt-confirm">This person&rsquo;s result is not on the file yet. Refresh to check again.</p>
      ) : (
        <div className="lt-form">
          <section className="vr-part" aria-label="Identity check">
            <span className="lt-label">Identity check</span>
            <PersonStatus status={person.id_check} />
            {person.id_check.detail !== undefined && person.id_check.detail.trim() !== "" && (
              <p className="lt-confirm">{person.id_check.detail}</p>
            )}
          </section>
          <section className="vr-part" aria-label="Screening">
            <span className="lt-label">Screening</span>
            <PersonStatus status={person.screening} />
            {hits.length > 0 && (
              <ul className="vr-hits">
                {hits.map((hit, index) => (
                  <li key={`${hit.list}-${index}`} className="lt-confirm">
                    {hitLine(hit)}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}

/**
 * Card 3357 — the REASON dialog (Clear's, before the card): the sentence, and a REQUIRED reason —
 * never blank, the button stays off until there is one. Every word is the reference's (`label`,
 * `confirm`, `wording`), else the page's generic ones.
 */
export function ReasonDialog({
  title,
  description,
  prompt,
  placeholder,
  primaryLabel,
  viewer,
  pending,
  error,
  onCancel,
  onSave,
}: {
  title: string;
  description: string;
  prompt: string;
  placeholder?: string;
  primaryLabel: string;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (reason: string) => void;
}): JSX.Element {
  const [reason, setReason] = useState("");
  const ready = reason.trim() !== "";
  return (
    <ResolveDialog
      title={title}
      description={description}
      viewer={viewer}
      primaryLabel={primaryLabel}
      pending={pending}
      canSubmit={ready}
      error={error}
      onCancel={onCancel}
      onSubmit={() => onSave(reason.trim())}
    >
      <label className="lt-field">
        <span className="lt-label">{prompt}</span>
        <Textarea
          rows={3}
          required
          autoFocus
          placeholder={placeholder}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
    </ResolveDialog>
  );
}

/**
 * Confirm removal — the people the client removed, listed, cleared in ONE act. The engine reads who
 * they are itself; the list here is only so the staffer sees whom they are confirming.
 */
export function ConfirmRemovalDialog({
  people,
  primaryLabel,
  viewer,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  people: readonly JobStatusOpenItemPerson[];
  /** The reference's label (the button that opened it). */
  primaryLabel: string;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string | null) => void;
}): JSX.Element {
  const [notes, setNotes] = useState("");
  const count = people.length;
  return (
    <ResolveDialog
      title="Confirm the client's removals"
      description={
        count === 1
          ? "The client removed this person, who is listed at Companies House. Confirming clears them from the file's checks."
          : `The client removed these ${count} people, who are listed at Companies House. Confirming clears them all from the file's checks.`
      }
      viewer={viewer}
      primaryLabel={primaryLabel}
      pending={pending}
      error={error}
      onCancel={onCancel}
      onSubmit={() => onConfirm(notes.trim() === "" ? null : notes.trim())}
    >
      <ul className="rd-people" aria-label="People the client removed">
        {people.map((person) => (
          <li key={person.party_key}>{person.name}</li>
        ))}
      </ul>
      <label className="lt-field">
        <span className="lt-label">
          Notes <span className="lt-optional">(optional)</span>
        </span>
        <Textarea
          rows={2}
          placeholder="Anything the client told you about why"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
    </ResolveDialog>
  );
}

/** Restore — pick ONE of the people the client removed and put them back on the file. */
export function RestoreDialog({
  people,
  initial,
  primaryLabel,
  viewer,
  pending,
  error,
  onCancel,
  onRestore,
}: {
  people: readonly JobStatusOpenItemPerson[];
  initial: string | null;
  /** The reference's label (the button that opened it). */
  primaryLabel: string;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onRestore: (person: JobStatusOpenItemPerson) => void;
}): JSX.Element {
  const first = people.find((person) => person.party_key === initial) ?? people[0];
  const [chosen, setChosen] = useState<string>(first?.party_key ?? "");
  const person = people.find((candidate) => candidate.party_key === chosen);
  return (
    <ResolveDialog
      title="Restore someone the client removed"
      description="They go back on the file and need an email address before their identity form can be sent."
      viewer={viewer}
      primaryLabel={primaryLabel}
      pending={pending}
      canSubmit={person !== undefined}
      error={error}
      onCancel={onCancel}
      onSubmit={() => {
        if (person !== undefined) onRestore(person);
      }}
    >
      <label className="lt-field">
        <span className="lt-label">Who should stay on the file?</span>
        <Select
          options={people.map((candidate) => ({ value: candidate.party_key, label: candidate.name }))}
          value={chosen}
          onValueChange={setChosen}
        />
      </label>
    </ResolveDialog>
  );
}

/** Add email — one address, checked for shape here and again by the engine; it sends their form. */
export function AddEmailDialog({
  name,
  viewer,
  pending,
  error,
  onCancel,
  onSave,
}: {
  name: string;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (email: string) => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  return (
    <ResolveDialog
      title={`Add an email address for ${name}`}
      description="We send them their identity form at this address."
      viewer={viewer}
      note="This sends their form."
      primaryLabel="Send their form"
      pendingLabel="Sending…"
      pending={pending}
      canSubmit={email.trim() !== ""}
      error={problem ?? error}
      onCancel={onCancel}
      onSubmit={() => {
        if (!looksLikeEmail(email)) {
          setProblem("Check the email address: it should look like name@example.com.");
          return;
        }
        setProblem(null);
        onSave(email.trim());
      }}
    >
      <label className="lt-field">
        <span className="lt-label">Email address</span>
        <Input
          variant="text"
          type="email"
          autoFocus
          autoComplete="off"
          placeholder="name@example.com"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setProblem(null);
          }}
        />
      </label>
    </ResolveDialog>
  );
}

// ── Deep links ───────────────────────────────────────────────────────────────────────────

/**
 * The open item and action reference a deep link (`?action=<action>&party=<key>`) means. `party`
 * is the job_parties iteration key — the key the Chat alerts compose from `ctx.party.id`, and the
 * `party_key` every engine target and open-item key carries. Only references this viewer may take
 * (`allowed`) are candidates, tried most-specific first:
 * <ol>
 *   <li>a reference whose `target.party_key` is the key (a look-through for THAT company owner, a
 *       reminder to THAT person);</li>
 *   <li>a reference whose `target.item_key`, or an item whose `key`, is EXACTLY the key itself,
 *       `party:<key>` (their identity form) or `<item>:<key>` where `<item>` is ONE declared item
 *       id (a per-party item, `StaffItemKeys.PartyItem`) — never a loose suffix, so a bare
 *       `party=3` cannot match `party:director:3` or `personal_codes:director:3`;</li>
 *   <li>an item owed by that party;</li>
 *   <li>card 2404: a grouped item that names that party among its `people` (a Restore link for one
 *       of the people the client removed).</li>
 * </ol>
 * No `party` ⇒ the first allowed reference of that action. Nothing matches ⇒ undefined, and the
 * page just shows the file.
 */
export function findDeepLinkTarget(
  items: readonly JobStatusOpenItem[],
  action: string,
  party: string | null,
  { includeDisallowed = false }: { includeDisallowed?: boolean } = {},
): { item: JobStatusOpenItem; ref: JobStatusActionRef } | undefined {
  const candidates = items.flatMap((item) =>
    item.actions
      .filter((ref) => ref.action === action && (includeDisallowed || ref.allowed))
      .map((ref) => ({ item, ref })),
  );
  if (party === null) return candidates[0];
  const keyMatches = (key: string | undefined): boolean =>
    key !== undefined &&
    (key === party || key === `party:${party}` || isPerPartyItemKey(key, party));
  return (
    candidates.find(({ ref }) => ref.target.party_key === party) ??
    candidates.find(({ item, ref }) => keyMatches(ref.target.item_key) || keyMatches(item.key)) ??
    candidates.find(({ item }) => item.owed_by.party_key === party) ??
    candidates.find(({ item }) => (item.people ?? []).some((person) => person.party_key === party))
  );
}

/** `<item>:<party>` exactly, with `<item>` one declared item id (no `:`, not a key prefix). */
function isPerPartyItemKey(key: string, party: string): boolean {
  const suffix = `:${party}`;
  if (!key.endsWith(suffix)) return false;
  const itemId = key.slice(0, key.length - suffix.length);
  return /^[A-Za-z0-9_]+$/.test(itemId) && itemId !== "party" && itemId !== "form";
}

// ── The controller ───────────────────────────────────────────────────────────────────────

/**
 * The dialog open now, by its KIND (card 3357: the engine's dialog kinds, never an action id).
 * `confirm` also serves the panel's actions (no item).
 */
type OpenDialog =
  | { kind: "confirm"; ref: JobStatusActionRef; item?: JobStatusOpenItem }
  // A deep link to an action that runs on the click, or to a form link: nobody has clicked, so the
  // page asks first (the small confirm).
  | { kind: "ask"; ref: JobStatusActionRef; item: JobStatusOpenItem }
  | { kind: "form-ref"; ref: JobStatusActionRef; item: JobStatusOpenItem }
  | { kind: "reason"; ref: JobStatusActionRef; item: JobStatusOpenItem }
  | { kind: "look_through"; ref: JobStatusActionRef; item: JobStatusOpenItem }
  | { kind: "removal"; ref: JobStatusActionRef; item: JobStatusOpenItem }
  // Card 2404 — `party` is who it is about (for a pick: who a deep link asked for, else the first
  // person listed).
  | { kind: "result" | "email"; ref: JobStatusActionRef; item: JobStatusOpenItem; party: string }
  | { kind: "pick_party"; ref: JobStatusActionRef; item: JobStatusOpenItem; party: string | null };

interface ToastState {
  id: number;
  message: string;
  tone: "success" | "error";
}

/**
 * Everything the file page needs from the actions: per-item buttons, the panel's actions (the
 * engine's `panel_actions` — "Send reminders" to everyone owed), a line while a deep link's item
 * is being looked for, and the overlays.
 */
export interface FileActions {
  actionsFor: (item: JobStatusOpenItem) => ReactNode;
  panelActions: ReactNode;
  linkNotice: string | null;
  overlays: ReactNode;
}

/**
 * The file page's actions, wired to the contract's endpoints for this tenant and file. `detail` is
 * the file as last read; `viewer` is the signed-in email ("Recorded as …").
 */
export function useFileActions({
  tenantId,
  jobId,
  detail,
  viewer,
  refetch,
}: {
  tenantId: string | null;
  jobId: string | undefined;
  detail: JobStatusDetailResponse | undefined;
  viewer: string | null;
  /** Re-read the file — used while a deep link waits for its item to appear. */
  refetch?: () => void;
}): FileActions {
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const toastId = useRef(0);

  // Card 3357 — ONE post for every action: the reference's own route, its own target as the body.
  const act = useJobAction(tenantId, jobId, postStaffAction);

  const say = useCallback((message: string, tone: "success" | "error"): void => {
    toastId.current += 1;
    setToast({ id: toastId.current, message, tone });
  }, []);

  const dismissToast = useCallback((): void => setToast(null), []);

  const close = useCallback((): void => {
    setDialog(null);
    setDialogError(null);
  }, []);

  /**
   * A failed action. An input-bearing dialog keeps its input unless the answer makes it moot; a
   * "only X can do this" answer stays INSIDE whatever dialog is open (a toast would take the names
   * away after a few seconds); anything else is a toast.
   */
  const failed = (error: unknown, keepDialogOpen: boolean): void => {
    const failure = describeActionError(error);
    const keep =
      (keepDialogOpen && (failure.kind === "failed" || failure.kind === "rejected")) ||
      (dialog !== null && failure.kind === "role_required");
    if (keep) {
      setDialogError(failure.message);
      return;
    }
    say(failure.message, "error");
    close();
  };

  /**
   * Post one action to its own route, then say what happened. The body is the reference's own
   * target (`actionBody`) unless the dialog built the whole contract body itself (the onboarding
   * family's dialogs: a look-through record, an email, the person picked, the removal note);
   * `words` reads the answer (by default, by its shape: `resultWords`).
   */
  const run = (
    ref: JobStatusActionRef,
    {
      body = actionBody(ref),
      keepDialogOpen = false,
      words = (result: unknown) => resultWords(ref, result),
    }: {
      body?: Record<string, unknown>;
      keepDialogOpen?: boolean;
      words?: (result: unknown) => { message: string; ok: boolean };
    } = {},
  ): void => {
    setDialogError(null);
    act.mutate(
      { action: ref.action, body },
      {
        onSuccess: (result) => {
          const said = words(result);
          say(said.message, said.ok ? "success" : "error");
          close();
        },
        onError: (error) => failed(error, keepDialogOpen),
      },
    );
  };

  const nameOf = (party: string | null, item?: JobStatusOpenItem): string => personName(detail, party, item);

  /** Open the right thing for a reference, by its dialog kind: a dialog, a confirm, or the action. */
  const open = (item: JobStatusOpenItem, ref: JobStatusActionRef, fromLink: boolean): boolean => {
    if (!ref.allowed) return false;
    const { dialog: kind } = renderingOf(ref);
    switch (kind) {
      // Card 2404: every decision about one person opens its dialog, clicked or linked alike.
      case "result":
      case "email": {
        const party = partyOf(item, ref);
        if (party === null) return false;
        setDialogError(null);
        setDialog({ kind, item, ref, party });
        return true;
      }
      case "removal":
        setDialogError(null);
        setDialog({ kind: "removal", item, ref });
        return true;
      case "pick_party": {
        if ((item.people ?? []).length === 0) return false;
        const linked = fromLink ? wantedParty : null;
        setDialogError(null);
        setDialog({ kind: "pick_party", item, ref, party: ref.target.party_key ?? linked });
        return true;
      }
      case "look_through":
        setDialogError(null);
        setDialog({ kind: "look_through", item, ref });
        return true;
      case "reason":
        setDialogError(null);
        setDialog({ kind: "reason", item, ref });
        return true;
      case "form-ref":
        if (ref.url === undefined) return false;
        // A click follows the link itself; a deep link asks first.
        if (fromLink) setDialog({ kind: "form-ref", item, ref });
        return true;
      case "none":
        if (fromLink) {
          setDialogError(null);
          setDialog({ kind: "ask", item, ref });
        } else {
          run(ref);
        }
        return true;
      default:
        // `confirm`, and any kind this page does not know (a newer engine): a plain confirm.
        setDialogError(null);
        setDialog({ kind: "confirm", item, ref });
        return true;
    }
  };

  // ── Deep links: `?action=<action>&party=<key>` opens that action's dialog once. ──────────
  const [searchParams, setSearchParams] = useSearchParams();
  const consumed = useRef(false);
  const wanted = searchParams.get("action");
  const wantedParty = searchParams.get("party");
  // Waiting for the item a link names (not open yet, or already dealt with): re-read the file.
  const [linkWaiting, setLinkWaiting] = useState(false);
  const [linkGaveUp, setLinkGaveUp] = useState(false);
  const linkNotice = linkWaiting ? DEEP_LINK_PENDING : linkGaveUp ? DEEP_LINK_GAVE_UP : null;

  const finishLink = (): void => {
    consumed.current = true;
    setLinkWaiting(false);
    // The link has done its job: take it out of the address bar so a refresh does not reopen it.
    const params = new URLSearchParams(searchParams);
    params.delete("action");
    params.delete("party");
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (consumed.current || wanted === null || detail === undefined) return;
    const items = detail.open_items ?? [];
    const match = findDeepLinkTarget(items, wanted, wantedParty);
    if (match !== undefined) {
      finishLink();
      open(match.item, match.ref, true);
      return;
    }
    // The item is there but this viewer may not take it: show the file, as before. Only a link
    // whose item is not on the file at all is worth waiting for.
    const present = findDeepLinkTarget(items, wanted, wantedParty, { includeDisallowed: true });
    if (present !== undefined || refetch === undefined || linkGaveUp) {
      finishLink();
      return;
    }
    setLinkWaiting(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, wanted, wantedParty, linkGaveUp]);

  useEffect(() => {
    if (!linkWaiting || refetch === undefined) return;
    const started = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started >= DEEP_LINK_WAIT_MS) {
        clearInterval(timer);
        setLinkWaiting(false);
        setLinkGaveUp(true);
        return;
      }
      refetch();
    }, DEEP_LINK_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkWaiting]);

  // ── Per-item buttons ────────────────────────────────────────────────────────────────────
  const actionsFor = (item: JobStatusOpenItem): ReactNode => {
    const refs = drawableRefs(item);
    if (refs.length === 0) return null;
    // The viewer's own item leads with the ink button, as the design has it; anyone else's items
    // carry quiet ones.
    const lead = item.owed_by.kind === "staff" ? "primary" : "ghost";
    return refs.map((ref, index) => {
      const r = renderingOf(ref);
      const variant = index === 0 ? lead : "ghost";
      const key = `${ref.action}-${index}`;
      if (r.dialog === "form-ref") {
        return (
          <a
            key={key}
            className={`btn btn--${variant}`}
            href={ref.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {r.label}
          </a>
        );
      }
      return (
        <Button
          key={key}
          variant={variant}
          // An action that runs on the click cannot be clicked twice while it is on its way.
          disabled={r.dialog === "none" && act.isPending}
          onClick={() => open(item, ref, false)}
        >
          {r.label}
        </Button>
      );
    });
  };

  // ── The panel: the engine's file-level actions ("Send reminders" to everyone owed) ─────────
  const panelRefs = (detail?.panel_actions ?? []).filter((ref) => ref.allowed);
  const panelActions: ReactNode =
    panelRefs.length > 0
      ? panelRefs.map((ref, index) => {
          const r = renderingOf(ref);
          return (
            <Button
              key={`${ref.action}-${index}`}
              variant="ghost"
              disabled={act.isPending}
              onClick={() => {
                if (r.dialog === "none") {
                  run(ref);
                  return;
                }
                setDialogError(null);
                setDialog({ kind: "confirm", ref });
              }}
            >
              {r.label}
            </Button>
          );
        })
      : null;

  // ── Overlays ────────────────────────────────────────────────────────────────────────────
  const clientName = detail?.client_name ?? "this client";
  let overlay: ReactNode = null;
  if (dialog?.kind === "confirm") {
    // The confirm: the approved ResolveDialog screen, its sentence verbatim as the body.
    const { ref } = dialog;
    const r = renderingOf(ref);
    overlay = (
      <ResolveDialog
        title={headingOf(r)}
        viewer={viewer}
        primaryLabel={submitOf(r)}
        pendingLabel="Working…"
        pending={act.isPending}
        error={dialogError}
        onCancel={close}
        onSubmit={() => run(ref)}
      >
        <p className="lt-confirm">{r.confirm ?? GENERIC_CONFIRM_BODY}</p>
      </ResolveDialog>
    );
  } else if (dialog?.kind === "ask") {
    const { ref } = dialog;
    const r = renderingOf(ref);
    overlay = (
      <ConfirmDialog
        title={headingOf(r)}
        body={r.confirm ?? GENERIC_CONFIRM_BODY}
        confirmLabel={submitOf(r)}
        pending={act.isPending}
        error={dialogError}
        onCancel={close}
        onConfirm={() => run(ref)}
      />
    );
  } else if (dialog?.kind === "form-ref") {
    const r = renderingOf(dialog.ref);
    overlay = (
      <ConfirmDialog
        title={headingOf(r)}
        body={r.confirm ?? GENERIC_FORM_LINK_BODY}
        confirmLabel={submitOf(r)}
        href={dialog.ref.url}
        pending={false}
        onCancel={close}
        onConfirm={close}
      />
    );
  } else if (dialog?.kind === "reason") {
    const { ref } = dialog;
    const r = renderingOf(ref);
    overlay = (
      <ReasonDialog
        title={headingOf(r)}
        description={r.confirm ?? GENERIC_REASON_BODY}
        prompt={r.wording.prompt ?? GENERIC_REASON_PROMPT}
        placeholder={r.wording.placeholder}
        primaryLabel={submitOf(r)}
        viewer={viewer}
        pending={act.isPending}
        error={dialogError}
        onCancel={close}
        onSave={(reason) => run(ref, { body: actionBody(ref, { reason }), keepDialogOpen: true })}
      />
    );
  } else if (dialog?.kind === "look_through") {
    const { ref, item } = dialog;
    const partyKey = ref.target.party_key ?? item.owed_by.party_key ?? "";
    const owner =
      detail?.people?.find((person) => person.party_key === partyKey)?.name ??
      detail?.parties.find((party) => party.iteration_key === partyKey)?.display_name ??
      "A company";
    overlay = (
      <LookThroughDialog
        ownerName={owner}
        clientName={clientName}
        partyKey={partyKey}
        viewer={viewer}
        pending={act.isPending}
        error={dialogError}
        onCancel={close}
        // The dialog builds the whole record (its own party key included): it is the body.
        onSave={(record) => run(ref, { body: { ...record }, keepDialogOpen: true })}
      />
    );
  } else if (dialog?.kind === "result") {
    const { item, party } = dialog;
    // From the result, the item's other decisions that open in place are one click away.
    const next = drawableRefs(item)
      .filter((ref) => IN_PLACE_KINDS.has(renderingOf(ref).dialog))
      .map((ref) => ({
        label: renderingOf(ref).label,
        open: () => {
          open(item, ref, false);
        },
      }));
    overlay = (
      <ViewResultDialog
        person={detail?.people?.find((person) => person.party_key === party)}
        fallbackName={nameOf(party, item)}
        next={next}
        onClose={close}
      />
    );
  } else if (dialog?.kind === "removal") {
    const { ref } = dialog;
    const people = dialog.item.people ?? [];
    const count = people.length;
    overlay = (
      <ConfirmRemovalDialog
        people={people}
        primaryLabel={submitOf(renderingOf(ref))}
        viewer={viewer}
        pending={act.isPending}
        error={dialogError}
        onCancel={close}
        onConfirm={(reason) =>
          run(ref, {
            // The engine reads WHO was removed itself: the page sends only the optional note.
            body: reason === null ? {} : { reason },
            keepDialogOpen: true,
            words: () => ({
              message:
                count === 1
                  ? "Removal confirmed. They are cleared from the file's checks."
                  : `Removals confirmed. All ${count} are cleared from the file's checks.`,
              ok: true,
            }),
          })
        }
      />
    );
  } else if (dialog?.kind === "pick_party") {
    const { ref } = dialog;
    overlay = (
      <RestoreDialog
        people={dialog.item.people ?? []}
        initial={dialog.party}
        primaryLabel={submitOf(renderingOf(ref))}
        viewer={viewer}
        pending={act.isPending}
        error={dialogError}
        onCancel={close}
        onRestore={(person) =>
          run(ref, {
            body: { party_key: person.party_key },
            keepDialogOpen: true,
            words: () => ({
              message: `${person.name} is back on the file. Add their email address to send their form.`,
              ok: true,
            }),
          })
        }
      />
    );
  } else if (dialog?.kind === "email") {
    const { ref, item, party } = dialog;
    overlay = (
      <AddEmailDialog
        name={nameOf(party, item)}
        viewer={viewer}
        pending={act.isPending}
        error={dialogError}
        onCancel={close}
        onSave={(address) =>
          run(ref, {
            body: { party_key: party, email: address },
            keepDialogOpen: true,
            words: () => ({
              message: `${nameOf(party, item)}'s identity form is on its way to ${address}.`,
              ok: true,
            }),
          })
        }
      />
    );
  }

  const overlays = (
    <>
      {overlay}
      {toast !== null && (
        <Toast
          key={toast.id}
          message={toast.message}
          tone={toast.tone}
          durationMs={toast.tone === "error" ? ERROR_TOAST_MS : undefined}
          onDismiss={dismissToast}
        />
      )}
    </>
  );

  return { actionsFor, panelActions, linkNotice, overlays };
}
